import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

import { isNoIndex } from "../../store/environment"

// De taalcodes zoals zoekmachines ze kennen. Papiaments heeft geen landvariant.
const OG_LOCALES = { en: "en_US", nl: "nl_NL", pa: "pap" }

// De beschrijving die in het zoekresultaat onder de titel komt, per openbare pagina. Op een plek,
// want het is verkooptekst en geen paginalogica, en zo staat naast elkaar wat naast elkaar hoort.
// De rest van de app staat in robots.txt op `Disallow` en heeft er niets aan.
const DESCRIPTIONS = [
  { match: /^\/$/, key: "Seo_description_home" },
  { match: /^\/about/, key: "Seo_description_about" },
  { match: /^\/dive-deep/, key: "Seo_description_dive_deep" },
  { match: /^\/partners/, key: "Seo_description_partners" },
  { match: /^\/terms/, key: "Seo_description_terms" }
]

// Bestaande tag aanpassen, en alleen aanmaken als hij er nog niet is. Niet via JSX: React 19 hijst
// een `<meta>` wel naar de `<head>`, maar ruimt de gelijknamige tag uit `index.html` niet op, en dan
// staan er twee met verschillende inhoud.
const upsert = (tag, identifier, attribute, value) => {
  const [key, name] = identifier
  let element = document.head.querySelector(`${tag}[${key}="${name}"]`)

  if (!element) {
    element = document.createElement(tag)
    // Via `setAttribute`: `property` (die van Open Graph) bestaat niet als DOM-eigenschap van een
    // `<meta>`, dus toewijzen zet wel een veld op het object maar geen attribuut in de HTML.
    element.setAttribute(key, name)
    document.head.appendChild(element)
  }

  element.setAttribute(attribute, value)
}

/**
 * De metagegevens die van de route en de taal afhangen. De deelkaarten voor sociale media staan
 * juist in `index.html`: die crawlers voeren geen JavaScript uit.
 *
 * De canonieke URL komt uit `window.location.origin`, zodat test en productie naar zichzelf wijzen.
 * Dat een testomgeving zichzelf laat indexeren is niet de bedoeling; daar is `REACT_APP_NOINDEX` voor.
 */
export const Seo = ({ language }) => {
  const { pathname } = useLocation()
  const { t } = useTranslation()

  useEffect(() => {
    upsert("link", ["rel", "canonical"], "href", `${window.location.origin}${pathname}`)

    const description = DESCRIPTIONS.find(entry => entry.match.test(pathname))
    if (description) {
      upsert("meta", ["name", "description"], "content", t(description.key))
    }

    upsert("meta", ["name", "robots"], "content", isNoIndex() ? "noindex,nofollow" : "index,follow")
  }, [pathname, t, language])

  // `<html lang>` valt buiten React's boom. Een verkeerde taalcode laat een zoekmachine de pagina
  // aan het verkeerde publiek tonen, en schermlezers spreken hem verkeerd uit.
  useEffect(() => {
    if (!language) return

    document.documentElement.lang = language
    upsert("meta", ["property", "og:locale"], "content", OG_LOCALES[language] || OG_LOCALES.en)
  }, [language])

  return null
}
